<template>
  <div class="page-wrap" v-loading="loading">
    <el-card shadow="never" class="paper-info-card">
      <el-alert
        v-if="me && paper.id && !editable"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom:12px"
      >
        <template slot="title">
          该试卷由管理员或其他教师创建，您仅可查看题目和导出。可复制试卷 ID（{{ paper.id }}）到「新建试卷」中引用导入题目。
        </template>
      </el-alert>
      <div class="info-header">
        <div>
          <el-button type="text" icon="el-icon-arrow-left" @click="$router.push('/teacher/papers')" style="padding:0;margin-right:8px">返回</el-button>
          <span class="paper-name">{{ paper.title }}</span>
          <el-tag :type="paperStatusType(paper.status)" size="small" style="margin-left:8px">
            {{ paperStatusLabel(paper.status) }}
          </el-tag>
        </div>
        <div class="info-actions" v-if="editable">
          <span class="q-count-label">共 <strong>{{ questions.length }}</strong> 题</span>
          <el-button size="small" icon="el-icon-download" :disabled="questions.length === 0" :loading="exportingStudent" @click="exportPaper('student')">导出学生版</el-button>
          <el-button size="small" icon="el-icon-download" :disabled="questions.length === 0" :loading="exportingTeacher" @click="exportPaper('teacher')">导出教师版</el-button>
          <el-button type="primary" size="small" :disabled="questions.length === 0" @click="submitReview">提交审核</el-button>
        </div>
        <div class="info-actions" v-else>
          <span class="q-count-label">共 <strong>{{ questions.length }}</strong> 题</span>
          <el-button size="small" icon="el-icon-download" :disabled="questions.length === 0" :loading="exportingStudent" @click="exportPaper('student')">导出学生版</el-button>
          <el-button size="small" icon="el-icon-download" :disabled="questions.length === 0" :loading="exportingTeacher" @click="exportPaper('teacher')">导出教师版</el-button>
        </div>
      </div>
      <div v-if="paper.description" class="paper-desc">{{ paper.description }}</div>
      <div v-if="paper.status === 3 && paper.auditRemark" class="audit-remark">
        驳回原因：{{ paper.auditRemark }}
      </div>
      <div v-if="paper.status === 2" class="audit-hint">试卷已提交审核，请等待管理员处理。</div>
    </el-card>

    <el-card shadow="never" style="margin-top:12px">
      <div slot="header" class="card-header">
        <span>{{ editable ? '已组题目' : '试卷题目' + (editable ? '' : '（只读）') }}</span>
        <div class="card-header-actions">
          <span class="source-switch-wrap">
            <span class="source-switch-label">显示来源</span>
            <el-switch v-model="showQuestionSource" />
          </span>
          <el-button v-if="editable" size="small" type="primary" icon="el-icon-plus" @click="openAddDialog">从题库添加</el-button>
        </div>
      </div>
      <el-empty v-if="!loading && questions.length === 0" :description="editable ? '暂无题目，点击「从题库添加」开始组卷' : '该试卷暂无题目'" />
      <div v-for="(q, idx) in questions" :key="q.id" class="q-row">
        <div class="q-row-left">
          <span class="q-index">{{ idx + 1 }}</span>
          <div class="q-info">
            <div class="q-meta-line">
              <DifficultyStars :value="q.difficulty" readonly compact />
              <div v-if="showQuestionSource && formatPaperSource(q)" class="q-source">{{ formatPaperSource(q) }}</div>
            </div>
            <div class="q-content-text rich-html" v-html="renderHtml(q.content)"></div>
            <div class="q-options" v-if="parseOptions(q.optionsJson).length">
              <div v-for="(opt, i) in parseOptions(q.optionsJson)" :key="i" class="q-option-item">
                <span class="q-option-key">{{ opt.key }}.</span>
                <div class="q-option-value rich-html" v-html="renderHtml(opt.value)"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="editable" class="q-row-actions">
          <el-button type="text" icon="el-icon-top" :disabled="idx === 0 || orderSaving" @click="moveQuestion(idx, -1)">上移</el-button>
          <el-button type="text" icon="el-icon-bottom" :disabled="idx === questions.length - 1 || orderSaving" @click="moveQuestion(idx, 1)">下移</el-button>
          <el-button type="text" icon="el-icon-delete" class="danger-text" @click="removeQuestion(q)">移除</el-button>
        </div>
      </div>
    </el-card>

    <el-dialog title="从题库添加题目" :visible.sync="addDialogVisible" width="1040px" top="4vh" custom-class="add-q-dialog">
      <div class="add-dialog-body">
        <div class="add-main">
          <div class="add-filter">
            <el-select v-model="addFilter.gradeId" placeholder="年级" clearable style="width:110px" @change="onAddFilterChange">
              <el-option v-for="g in grades" :key="g.id" :label="g.name" :value="g.id" />
            </el-select>
            <el-cascader
              v-model="addFilter.knowledgeTagPath"
              :options="knowledgeTagOptions"
              :props="{ value: 'value', label: 'label', children: 'children', checkStrictly: true, emitPath: true }"
              placeholder="知识点"
              clearable
              filterable
              style="width:200px"
              @change="onKnowledgeTagChange"
            />
            <el-cascader
              v-model="addFilter.questionTagPath"
              :options="questionTagOptions"
              :props="{ value: 'value', label: 'label', children: 'children', checkStrictly: true, emitPath: true }"
              placeholder="题型标签"
              clearable
              filterable
              style="width:180px"
              @change="onQuestionTagChange"
            />
            <el-select v-model="addFilter.type" placeholder="题型" clearable style="width:100px" @change="onTypeFilterChange">
              <el-option v-for="(label, key) in typeMap" :key="key" :label="label" :value="key" />
            </el-select>
            <el-input v-model="addFilter.keyword" placeholder="搜索题目关键词" clearable style="width:180px"
              @keyup.enter.native="onAddFilterChange" @clear="onAddFilterChange" />
          </div>
          <div v-loading="addLoading" class="add-question-list">
            <el-empty v-if="!addLoading && addQuestions.length === 0" description="暂无题目" />
            <div
              v-for="q in addQuestions" :key="q.id"
              class="add-q-row"
              :class="{ selected: isInBasket(q.id), added: addedIds.includes(q.id) }"
              @click="!addedIds.includes(q.id) && toggleSelect(q)"
            >
              <div class="add-q-check">
                <i v-if="addedIds.includes(q.id)" class="el-icon-check" style="color:#52c41a"></i>
                <el-checkbox v-else :value="isInBasket(q.id)" @change="toggleSelect(q)" @click.native.stop />
              </div>
              <div class="add-q-content">
                <div class="add-q-text rich-html" v-html="renderHtml(q.content)"></div>
                <div class="add-q-tags">
                  <el-tag size="mini">{{ typeMap[q.type] || q.type }}</el-tag>
                  <DifficultyStars :value="q.difficulty" readonly compact />
                  <el-tag v-for="tag in getLeafTagNames(q.knowledgeTags)" :key="'k-' + tag" size="mini" type="success">{{ tag }}</el-tag>
                  <el-tag v-for="tag in getLeafTagNames(q.questionTag)" :key="'q-' + tag" size="mini" type="info">{{ tag }}</el-tag>
                </div>
              </div>
              <el-button v-if="!addedIds.includes(q.id)" type="text" size="mini" @click.stop="addSingle(q.id)">加入</el-button>
              <span v-else style="font-size:12px;color:#52c41a;padding-right:8px">已加入</span>
            </div>
          </div>
          <el-pagination
            style="text-align:right;margin-top:8px"
            @current-change="handleAddPageChange"
            :current-page="addPage + 1"
            :page-size="addSize"
            :total="addTotal"
            layout="total, prev, pager, next"
            small
          />
        </div>
        <div class="add-basket-panel">
          <div class="basket-header">
            <span class="basket-title">试题篮（{{ basketItems.length }}）</span>
            <el-button type="text" size="mini" :disabled="!basketItems.length" @click="clearBasket">清空</el-button>
          </div>
          <div class="basket-list">
            <el-empty v-if="!basketItems.length" description="勾选题目加入试题篮，可跨页累计" :image-size="56" />
            <div v-for="(item, idx) in basketItems" :key="item.id" class="basket-item">
              <span class="basket-index">{{ idx + 1 }}</span>
              <div class="basket-item-body">
                <div class="basket-item-text rich-html" v-html="renderHtml(item.content)"></div>
                <div v-if="item.knowledgeTags" class="basket-item-tags">
                  <el-tag v-for="tag in getLeafTagNames(item.knowledgeTags)" :key="tag" size="mini" type="success">{{ tag }}</el-tag>
                </div>
              </div>
              <el-button type="text" icon="el-icon-close" class="basket-remove" @click="removeFromBasket(item.id)" />
            </div>
          </div>
          <el-button type="primary" style="width:100%;margin-top:8px" :disabled="!basketItems.length" @click="batchAdd">
            加入试卷（{{ basketItems.length }}）
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import teacherRequest from '../../utils/teacherRequest'
import { canTeacherEditPaper } from '../../utils/teacherOwnership'
import { renderHtml } from '../../utils/renderHtml'
import { parseQuestionOptions } from '../../utils/questionOptions'
import DifficultyStars from '../../components/DifficultyStars.vue'
import { getLeafTagNames } from '../../utils/tagDisplay'

export default {
  components: { DifficultyStars },
  name: 'TeacherPaperDetail',
  data() {
    return {
      me: null,
      paper: {},
      questions: [],
      grades: [],
      loading: false,
      addDialogVisible: false,
      addLoading: false,
      addFilter: {
        gradeId: null, keyword: '', type: null,
        knowledgeTagPath: [], knowledgeTagNodeId: null,
        questionTagPath: [], questionTagNodeId: null
      },
      addQuestions: [],
      addPage: 0,
      addSize: 10,
      addTotal: 0,
      basketItems: [],
      addedIds: [],
      knowledgeTagOptions: [],
      questionTagOptions: [],
      orderSaving: false,
      exportingStudent: false,
      exportingTeacher: false,
      showQuestionSource: true,
      typeMap: { SINGLE: '单选题', MULTI: '多选题', FILL: '填空题', QA: '问答题' }
    }
  },
  computed: {
    paperId() { return this.$route.params.id },
    editable() {
      if (!this.me || !this.paper.id) return false
      return canTeacherEditPaper(this.paper, this.me)
    }
  },
  created() {
    this.loadMe()
    this.loadGrades()
    this.loadPaper()
  },
  methods: {
    renderHtml,
    loadMe() {
      teacherRequest.get('/me').then(data => { this.me = data }).catch(() => {})
    },
    loadGrades() {
      teacherRequest.get('/meta/grades').then(data => { this.grades = data || [] }).catch(() => {})
    },
    loadPaper() {
      if (!this.paperId) return
      this.loading = true
      Promise.all([
        teacherRequest.get(`/papers/${this.paperId}`),
        teacherRequest.get(`/papers/${this.paperId}/questions`)
      ]).then(([paper, questions]) => {
        this.paper = paper || {}
        this.questions = questions || []
      }).catch(() => {
        this.paper = {}
        this.questions = []
      }).finally(() => { this.loading = false })
    },
    paperStatusLabel(status) {
      const map = { 0: '草稿', 1: '已发布', 2: '待审核', 3: '已驳回' }
      return map[status] || '未知'
    },
    paperStatusType(status) {
      if (status === 1) return 'success'
      if (status === 2) return 'warning'
      if (status === 3) return 'danger'
      return 'info'
    },
    parseOptions: parseQuestionOptions,
    removeQuestion(q) {
      this.$confirm('确认从试卷中移除该题目？', '提示', { type: 'warning' }).then(() => {
        teacherRequest.delete(`/papers/${this.paperId}/questions/${q.id}`).then(() => {
          this.$message.success('已移除')
          this.loadPaper()
        })
      }).catch(() => {})
    },
    moveQuestion(index, step) {
      const target = index + step
      if (target < 0 || target >= this.questions.length || this.orderSaving) return
      const list = [...this.questions]
      const temp = list[index]
      list[index] = list[target]
      list[target] = temp
      this.questions = list
      this.saveQuestionOrder()
    },
    saveQuestionOrder() {
      const questionIds = this.questions.map(q => q.id)
      this.orderSaving = true
      teacherRequest.put(`/papers/${this.paperId}/questions/order`, { questionIds }).then(() => {
        this.$message.success('顺序已更新')
      }).catch(e => {
        this.$message.error(e.message || '调整顺序失败')
        this.loadPaper()
      }).finally(() => { this.orderSaving = false })
    },
    formatPaperSource(q) {
      const source = q && q.source ? String(q.source).trim() : ''
      return source ? `来源：${source}` : ''
    },
    exportPaper(version) {
      const loadingKey = version === 'teacher' ? 'exportingTeacher' : 'exportingStudent'
      this[loadingKey] = true
      const token = localStorage.getItem('admin_token')
      const showSource = this.showQuestionSource ? 'true' : 'false'
      fetch(`/api/teacher/papers/${this.paperId}/export-pdf?version=${version}&showSource=${showSource}`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/pdf' }
      }).then(async res => {
        const contentType = res.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const err = await res.json()
          this.$message.error(err.message || '导出失败')
          return
        }
        if (!res.ok) {
          this.$message.error('导出失败，请稍后重试')
          return
        }
        const blob = await res.blob()
        if (!blob.size) {
          this.$message.error('导出文件为空')
          return
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const versionLabel = version === 'teacher' ? '教师版' : '学生版'
        a.download = `${this.paper.title || '试卷'}_${versionLabel}.pdf`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      }).catch(() => {
        this.$message.error('导出失败')
      }).finally(() => { this[loadingKey] = false })
    },
    submitReview() {
      this.$confirm('提交后需管理员审核通过方可发布，确认提交？', '提交审核', { type: 'warning' }).then(() => {
        teacherRequest.post(`/papers/${this.paperId}/submit-review`).then(paper => {
          this.$message.success('已提交审核')
          this.paper = paper || this.paper
          this.paper.status = 2
        })
      }).catch(() => {})
    },
    openAddDialog() {
      this.addDialogVisible = true
      this.basketItems = []
      this.addedIds = []
      this.addFilter = {
        gradeId: this.paper.gradeId || null,
        keyword: '',
        type: null,
        knowledgeTagPath: [],
        knowledgeTagNodeId: null,
        questionTagPath: [],
        questionTagNodeId: null
      }
      this.addPage = 0
      this.loadTagTrees()
      this.loadAddQuestions()
    },
    loadTagTrees() {
      this.loadKnowledgeTagTree()
      this.loadQuestionTagTree()
    },
    loadKnowledgeTagTree() {
      const params = { tagType: 'knowledge' }
      if (this.paper.subjectId) params.subjectId = this.paper.subjectId
      const stageId = this.resolveStageId()
      if (stageId) params.stageId = stageId
      teacherRequest.get('/tag-tree', { params }).then(tree => {
        this.knowledgeTagOptions = this.buildCascaderOptions(tree || [])
      }).catch(() => { this.knowledgeTagOptions = [] })
    },
    loadQuestionTagTree() {
      const params = { tagType: 'question' }
      if (this.paper.subjectId) params.subjectId = this.paper.subjectId
      const stageId = this.resolveStageId()
      if (stageId) params.stageId = stageId
      teacherRequest.get('/tag-tree', { params }).then(tree => {
        this.questionTagOptions = this.buildCascaderOptions(tree || [])
      }).catch(() => { this.questionTagOptions = [] })
    },
    resolveStageId() {
      const gradeId = this.addFilter.gradeId || this.paper.gradeId
      if (!gradeId) return null
      const g = this.grades.find(x => x.id === gradeId)
      return g && g.stageId ? g.stageId : null
    },
    buildCascaderOptions(nodes) {
      if (!nodes || !nodes.length) return []
      return nodes.map(n => ({
        value: n.id,
        label: n.name,
        children: n.children && n.children.length ? this.buildCascaderOptions(n.children) : undefined
      }))
    },
    getLeafTagNames,
    onKnowledgeTagChange(path) {
      this.addFilter.knowledgeTagNodeId = path && path.length ? path[path.length - 1] : null
      this.addPage = 0
      this.loadAddQuestions()
    },
    onQuestionTagChange(path) {
      this.addFilter.questionTagNodeId = path && path.length ? path[path.length - 1] : null
      this.addPage = 0
      this.loadAddQuestions()
    },
    onTypeFilterChange() {
      this.addPage = 0
      this.loadAddQuestions()
    },
    loadAddQuestions() {
      this.addLoading = true
      const { gradeId, keyword, type, knowledgeTagNodeId, questionTagNodeId } = this.addFilter
      teacherRequest.get('/questions/list', {
        params: {
          page: this.addPage, size: this.addSize, gradeId, keyword, type,
          knowledgeTagNodeId, questionTagNodeId, status: 1
        }
      }).then(data => {
        this.addQuestions = data.content || []
        this.addTotal = data.totalElements || 0
      }).finally(() => { this.addLoading = false })
    },
    onAddFilterChange() {
      this.addFilter.knowledgeTagPath = []
      this.addFilter.knowledgeTagNodeId = null
      this.addFilter.questionTagPath = []
      this.addFilter.questionTagNodeId = null
      this.addPage = 0
      this.loadTagTrees()
      this.loadAddQuestions()
    },
    handleAddPageChange(val) { this.addPage = val - 1; this.loadAddQuestions() },
    isInBasket(id) {
      return this.basketItems.some(i => i.id === id)
    },
    toggleSelect(q) {
      if (this.addedIds.includes(q.id)) return
      const idx = this.basketItems.findIndex(i => i.id === q.id)
      if (idx > -1) {
        this.basketItems.splice(idx, 1)
      } else {
        this.basketItems.push({
          id: q.id,
          content: q.content,
          knowledgeTags: q.knowledgeTags,
          questionTag: q.questionTag,
          type: q.type
        })
      }
    },
    removeFromBasket(id) {
      this.basketItems = this.basketItems.filter(i => i.id !== id)
    },
    clearBasket() {
      this.basketItems = []
    },
    addSingle(questionId) {
      teacherRequest.post(`/papers/${this.paperId}/questions`, { questionId }).then(() => {
        this.$message.success('已加入试卷')
        this.addedIds.push(questionId)
        this.removeFromBasket(questionId)
        this.loadPaper()
      })
    },
    batchAdd() {
      const questionIds = this.basketItems.map(i => i.id).filter(id => !this.addedIds.includes(id))
      if (!questionIds.length) return
      teacherRequest.post(`/papers/${this.paperId}/questions/batch`, { questionIds }).then(data => {
        this.$message.success(`已加入 ${data.added || 0} 题`)
        this.addedIds = [...this.addedIds, ...questionIds]
        this.basketItems = this.basketItems.filter(i => !questionIds.includes(i.id))
        this.loadPaper()
      })
    }
  }
}
</script>

<style scoped>
.paper-info-card { margin-bottom: 0; }
.info-header { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px; }
.paper-name { font-size: 16px; font-weight: 600; }
.paper-desc { margin-top: 8px; color: #666; font-size: 13px; }
.audit-remark { margin-top: 8px; padding: 8px 12px; background: #fff2f0; border-radius: 4px; color: #cf1322; font-size: 13px; }
.audit-hint { margin-top: 8px; color: #d48806; font-size: 13px; }
.info-actions { display: flex; align-items: center; gap: 12px; }
.q-count-label { font-size: 14px; color: #555; }
.card-header { display: flex; align-items: center; justify-content: space-between; }
.card-header-actions { display: flex; align-items: center; gap: 12px; }
.source-switch-wrap { display: inline-flex; align-items: center; gap: 6px; }
.source-switch-label { font-size: 13px; color: #666; }
.q-row { display: flex; align-items: flex-start; justify-content: space-between; padding: 12px 0; border-bottom: 1px solid #f0f0f0; }
.q-row-actions { display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; }
.q-row-actions .el-button { padding: 4px 0; margin: 0; }
.q-row-left { display: flex; gap: 12px; flex: 1; }
.q-index { width: 28px; height: 28px; line-height: 28px; text-align: center; background: #e6f4ff; color: #1677ff; border-radius: 4px; flex-shrink: 0; font-weight: 600; }
.q-info { flex: 1; min-width: 0; }
.q-meta-line { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 6px; }
.q-content-text { font-size: 14px; line-height: 1.6; margin-bottom: 8px; }
.q-source { font-size: 12px; color: #d48806; margin-bottom: 0; }
.q-options { padding-left: 4px; }
.q-option-item { font-size: 13px; color: #444; margin-bottom: 4px; display: flex; gap: 6px; }
.q-option-key { font-weight: 600; flex-shrink: 0; }
.danger-text { color: #ff4d4f; }
.add-filter { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 12px; }
.add-dialog-body { display: flex; gap: 12px; align-items: stretch; min-height: 460px; }
.add-main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.add-basket-panel {
  width: 268px; flex-shrink: 0; border-left: 1px solid #eee; padding-left: 12px;
  display: flex; flex-direction: column;
}
.basket-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.basket-title { font-size: 14px; font-weight: 600; color: #333; }
.basket-list { flex: 1; overflow-y: auto; max-height: 400px; min-height: 120px; }
.basket-item {
  display: flex; gap: 6px; align-items: flex-start; padding: 8px 4px;
  border-bottom: 1px solid #f5f5f5; font-size: 12px;
}
.basket-index { color: #1677ff; font-weight: 600; flex-shrink: 0; width: 18px; }
.basket-item-body { flex: 1; min-width: 0; }
.basket-item-text {
  line-height: 1.4; color: #444;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 3; overflow: hidden;
}
.basket-item-tags { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.basket-remove { padding: 0; color: #999; flex-shrink: 0; }
.add-question-list { max-height: 400px; overflow-y: auto; flex: 1; }
.add-q-row { display: flex; align-items: flex-start; gap: 8px; padding: 10px 8px; border-bottom: 1px solid #f5f5f5; cursor: pointer; }
.add-q-row:hover { background: #f0f5ff; }
.add-q-row.selected { background: #e6f4ff; }
.add-q-row.added { opacity: 0.6; cursor: default; }
.add-q-check { flex-shrink: 0; width: 20px; }
.add-q-content { flex: 1; min-width: 0; }
.add-q-text { font-size: 14px; line-height: 1.5; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; margin-bottom: 6px; }
.add-q-tags { display: flex; flex-wrap: wrap; gap: 4px; }
</style>
